export function getPropByListNotation<T = any>(sourceObject: Record<string | number, any>, listNotationPath: Array<string | number>): T {
  let returnData;
  let lastObjectSubset = sourceObject;

  listNotationPath.forEach((subPath, index) => {
    if (lastObjectSubset[subPath] === null || lastObjectSubset[subPath] === undefined) {
      throw new Error(`Property "${subPath}" not found`);
    }
    lastObjectSubset = lastObjectSubset[subPath];

    if (index === listNotationPath.length - 1) {
      returnData = lastObjectSubset;
    }
  });

  if (returnData === undefined) {
    throw new Error('Property not found');
  }

  return returnData;
}

/**
 * Reads nested attributes from an object using a dot-separated path.
 *
 * @param obj - The object to read from.
 * @param path - The path to the desired attribute (dot-separated).
 * @returns The value at the specified path or undefined if not found.
 *
 * @example
 * const data = { user: { profile: { name: "Alice" } } };
 * getPropByDotNotation(data, "user.profile.name"); // "Alice"
 *
 * @example
 * const config = { settings: { theme: { darkMode: true } } };
 * getPropByDotNotation(config, "settings.theme.darkMode"); // true
 *
 * @example
 * getPropByDotNotation({}, "non.existent.path"); // undefined
 *
 * @example
 * getPropByDotNotation(null, "some.path"); // undefined
 */
export function getPropByDotNotation(obj: {
  [key: string]: any,
}, path: string): any {
  if (!obj || !path) {
    return undefined;
  }
  return path.split('.').reduce((acc, key) => {
    return acc !== undefined ? acc[key] : undefined;
  }, obj);
}

// https://stackoverflow.com/a/47514598
export function objectDeepKeys(obj: Record<string, any>): string[] {
  return Object
    .keys(obj)
    .filter((key) => obj[key] instanceof Object)
    .map(
      (key) => objectDeepKeys(obj[key])
        .map((k) => `${key}.${k}`),
    )
    .reduce((x, y) => x.concat(y), Object.keys(obj));
}

export function flattenObject(obj: Record<string, any>, parentKey?: string, result: Record<string, any> = {}) {
  Object.keys(obj).forEach((key) => {
    const propName = parentKey ? `${parentKey}.${key}` : key;
    if (typeof obj[key] === 'object') {
      flattenObject(obj[key], propName, result);
    } else {
      result[propName] = obj[key];
    }
  });
  return result;
}
